/**
 * Snapshot management sidebar for the Linux Desktop Viewer.
 *
 * Renders a slide-out panel listing all VM snapshots for the current
 * desktop. Supports create (with displayName + description), restore
 * (launches a new desktop from a snapshot's registered AMI), and delete.
 *
 * All API/Redux interaction goes through ./store/desktopSnapshotSlice.
 * Errors are surfaced via antd's `message` toasts and inline within the
 * list item itself for state=Failed / Restorable=false.
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Drawer,
  Button,
  List,
  Tag,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  Spin,
  Empty,
  Typography,
  Space,
  Popconfirm,
  message
} from "antd";
import {
  CameraOutlined,
  ReloadOutlined,
  DeleteOutlined,
  HistoryOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  fetchDesktopSnapshots,
  createDesktopSnapshotThunk,
  deleteDesktopSnapshotThunk,
  restoreDesktopFromSnapshotThunk
} from "./store/desktopSnapshotSlice";

const { Text, Paragraph } = Typography;

const stateColor = (s) => {
  switch (s) {
    case "Ready":
      return "green";
    case "Failed":
      return "red";
    case "Pending":
    case "Creating":
    case "Stopping":
    case "Restarting":
    case "Registering":
      return "blue";
    default:
      return "default";
  }
};

const formatRelative = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

/**
 * @param {Object} props
 * @param {string} props.desktopId
 * @param {boolean} props.open
 * @param {Function} props.onClose
 */
function SnapshotSidebar({ desktopId, open, onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const bucket = useSelector((s) => s.desktopSnapshots?.byDesktop?.[desktopId]);
  const creating = useSelector((s) => s.desktopSnapshots?.creating);
  const restoring = useSelector((s) => s.desktopSnapshots?.restoring);
  const items = useMemo(() => bucket?.items || [], [bucket]);
  const loading = bucket?.loading;
  const error = bucket?.error;

  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();

  // Track items by ref so the polling effect can check current state
  // without re-subscribing (which would create an infinite fetch loop).
  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  const refresh = useCallback(() => {
    if (desktopId) {
      dispatch(fetchDesktopSnapshots(desktopId))
        .unwrap()
        .catch((err) => {
          // Surface the failure visibly — silent failures are why this
          // sidebar "doesn't work" from the user's perspective.
          // eslint-disable-next-line no-console
          console.error("[SnapshotSidebar] fetchDesktopSnapshots failed:", err);
        });
    }
  }, [dispatch, desktopId]);

  // (1) Initial load whenever the sidebar opens for a desktop.
  useEffect(() => {
    if (open && desktopId) refresh();
  }, [open, desktopId, refresh]);

  // (2) Poll every 8s while in-flight snapshots exist. Reads items via ref
  // so this effect doesn't re-run on every fetch (which would infinite-loop).
  useEffect(() => {
    if (!open || !desktopId) return undefined;
    const t = setInterval(() => {
      const current = itemsRef.current || [];
      const anyInFlight = current.some(
        (s) => s.state && s.state !== "Ready" && s.state !== "Failed"
      );
      if (anyInFlight) refresh();
    }, 8000);
    return () => clearInterval(t);
  }, [open, desktopId, refresh]);

  const handleCreate = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch (err) {
      // Antd validation error — fields are already highlighted.
      return;
    }
    try {
      const res = await dispatch(
        createDesktopSnapshotThunk({
          desktopId,
          displayName: v.displayName?.trim() || "",
          description: v.description?.trim() || "",
          consistency: v.consistency || "crash-consistent",
          visibility: v.visibility || "private"
        })
      ).unwrap();
      const label = res?.snapshot?.displayName || res?.snapshot?.id || "snapshot";
      message.success(`Snapshot "${label}" started`);
      setCreateOpen(false);
      form.resetFields();
      refresh();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SnapshotSidebar] create failed:", e);
      message.error(typeof e === "string" ? e : e?.message || "Failed to create snapshot");
    }
  };

  const handleDelete = async (snap) => {
    try {
      await dispatch(deleteDesktopSnapshotThunk({ name: snap.id, desktopId })).unwrap();
      message.success(`Deleted snapshot "${snap.displayName || snap.id}"`);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[SnapshotSidebar] delete failed:", e);
      message.error(typeof e === "string" ? e : e?.message || "Failed to delete snapshot");
    }
  };

  const handleRestore = async (snap) => {
    if (!snap.restorable) {
      message.warning(
        snap.notRestorableReason ||
          "Snapshot is not yet ready to restore. The AMI is still being registered."
      );
      return;
    }
    Modal.confirm({
      title: `Restore "${snap.displayName || snap.id}"?`,
      icon: <HistoryOutlined style={{ color: "#1677ff" }} />,
      content: (
        <div>
          <Paragraph>This launches a new Linux desktop using the snapshot as its boot disk.</Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 12 }}>
            AMI: {snap.imageId} &middot; Size: {snap.sizeGB || "?"} GiB
          </Paragraph>
        </div>
      ),
      okText: "Restore as new desktop",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const desk = await dispatch(
            restoreDesktopFromSnapshotThunk({
              name: snap.id,
              params: {
                template: "linux-dcv",
                instanceType: "g4dn.xlarge",
                persistent: true
              }
            })
          ).unwrap();
          message.success("Restoring — your new desktop is provisioning");
          if (desk?.id) navigate(`/control/linux/${desk.id}`);
        } catch (e) {
          message.error(typeof e === "string" ? e : e.message || "Failed to restore snapshot");
        }
      }
    });
  };

  const renderSnapshot = (snap) => (
    <List.Item
      key={snap.id}
      className="snapshot-item"
      actions={[
        <Tooltip title={snap.restorable ? "Restore as new desktop" : snap.notRestorableReason || "Not ready"} key="restore">
          <Button
            type="text"
            icon={<HistoryOutlined />}
            disabled={!snap.restorable || restoring}
            onClick={() => handleRestore(snap)}
          />
        </Tooltip>,
        <Popconfirm
          key="delete"
          title="Delete this snapshot?"
          description="This permanently removes the EBS snapshot and AMI. Existing desktops are not affected."
          okText="Delete"
          okType="danger"
          cancelText="Cancel"
          onConfirm={() => handleDelete(snap)}
        >
          <Button type="text" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ]}
    >
      <List.Item.Meta
        title={
          <Space size={6} wrap>
            <Text strong>{snap.displayName || snap.id}</Text>
            <Tag color={stateColor(snap.state)} style={{ marginInlineStart: 4 }}>
              {snap.state || "?"}
            </Tag>
            <Tooltip
              title={
                snap.visibility === "public"
                  ? "Visible to your whole organization"
                  : "Visible only to you"
              }
            >
              <Tag color={snap.visibility === "public" ? "geekblue" : "default"}>
                {snap.visibility === "public" ? "Public" : "Private"}
              </Tag>
            </Tooltip>
            {snap.sizeGB > 0 && (
              <Tag color="default" style={{ marginInlineStart: 0 }}>
                {snap.sizeGB} GiB
              </Tag>
            )}
            {snap.restorable === false && snap.state === "Ready" && (
              <Tooltip title={snap.notRestorableReason || "AMI not registered"}>
                <Tag icon={<ExclamationCircleOutlined />} color="warning">
                  Not restorable
                </Tag>
              </Tooltip>
            )}
          </Space>
        }
        description={
          <div style={{ marginTop: 2 }}>
            {snap.description && (
              <Paragraph type="secondary" style={{ marginBottom: 4, fontSize: 12 }}>
                {snap.description}
              </Paragraph>
            )}
            <Space size={12} style={{ fontSize: 11, color: "#888" }}>
              <span>
                <ClockCircleOutlined /> {formatRelative(snap.createdAt)}
              </span>
              {snap.imageId && (
                <Tooltip title={`AMI ${snap.imageId}`}>
                  <span>
                    <InfoCircleOutlined /> bootable
                  </span>
                </Tooltip>
              )}
              {snap.message && snap.state !== "Ready" && (
                <Tooltip title={snap.message}>
                  <span style={{ color: "#faad14" }}>{snap.message.slice(0, 60)}{snap.message.length > 60 ? "…" : ""}</span>
                </Tooltip>
              )}
            </Space>
          </div>
        }
      />
    </List.Item>
  );

  return (
    <>
      <Drawer
        title={
          <Space>
            <CameraOutlined />
            <span>Snapshots</span>
          </Space>
        }
        placement="right"
        width={420}
        open={open}
        onClose={onClose}
        closeIcon={<CloseOutlined />}
        zIndex={1100}
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button icon={<ReloadOutlined spin={!!loading} />} onClick={refresh} />
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              Create
            </Button>
          </Space>
        }
        styles={{ body: { padding: 12 } }}
      >
        {error && (
          <div style={{ padding: 8, background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 4, marginBottom: 12 }}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {typeof error === "string" ? error : "Failed to load snapshots"}
            </Text>
          </div>
        )}
        {loading && items.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <Spin />
          </div>
        ) : items.length === 0 ? (
          <Empty
            description={
              <span style={{ color: "#888" }}>
                No snapshots yet. Click <b>Create</b> to capture the current state of this desktop.
              </span>
            }
          />
        ) : (
          <List
            dataSource={items}
            renderItem={renderSnapshot}
            split
            size="small"
          />
        )}
      </Drawer>

      <Modal
        title="Create snapshot"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false);
          form.resetFields();
        }}
        onOk={handleCreate}
        confirmLoading={creating}
        okText="Take snapshot"
        cancelText="Cancel"
        forceRender
        zIndex={1200}
        afterClose={() => form.resetFields()}
      >
        <Paragraph type="secondary" style={{ fontSize: 12 }}>
          Captures the current EBS volumes of this desktop. A bootable AMI is automatically registered so the snapshot can be restored as a new desktop later.
        </Paragraph>
        <Form
          form={form}
          layout="vertical"
          initialValues={{ consistency: "crash-consistent", visibility: "private" }}
        >
          <Form.Item
            name="displayName"
            label="Name"
            rules={[
              { required: true, message: "Please enter a name" },
              { max: 128, message: "Max 128 characters" }
            ]}
          >
            <Input placeholder="e.g. before-dependency-upgrade" autoFocus maxLength={128} />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="What's special about this state?" maxLength={500} />
          </Form.Item>
          <Form.Item
            name="visibility"
            label="Visibility"
            tooltip="Private: only you can see and use this snapshot. Public: everyone in your organization can see it and launch desktops from it."
          >
            <Select
              options={[
                { label: "Private (only me)", value: "private" },
                { label: "Public (whole organization)", value: "public" }
              ]}
            />
          </Form.Item>
          <Form.Item
            name="consistency"
            label="Consistency"
            tooltip="crash-consistent is fast and live. stopped pauses the desktop briefly for an application-consistent snapshot."
          >
            <Select
              options={[
                { label: "Crash-consistent (live, fast)", value: "crash-consistent" },
                { label: "Stopped (app-consistent, brief downtime)", value: "stopped" }
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default SnapshotSidebar;
